// File: apps/web/src/routes/signup.tsx
// Version: 2026.4.9 | Path: apps/web/src/routes/signup.tsx
//
// PLAN-FIRST SIGNUP FLOW
// ────────────────────────────────────────────────────────────────────────────
// The /signup route has two distinct modes, routed by auth state:
//
//   UNAUTHENTICATED  →  renders <PlanSelectionPage mode="pre-auth" />
//                        User picks plan + cycle, clicks Continue.
//                        PlanSelectionPage navigates to /signup/authenticate
//                        with ?plan=...&cycle=... which fires the MSAL
//                        redirect with the plan encoded in MSAL state.
//
//   AUTHENTICATED    →  bootstrap → needs-workspace form → create-tenant
//                        The plan selection is recovered from
//                        useAuth().redirectState (set by the bridge page's
//                        login({ state: ... }) call, awaited properly by
//                        the updated @forgetrack/auth provider) and passed
//                        through to /api/v1/signup/create-tenant so the
//                        backend provisions with the right plan from the
//                        start — no half-state where a tenant exists
//                        without a plan.
//
//   For paid plans, after the tenant is created the backend also creates
//   a Stripe checkout session and returns the URL. For free plans, the
//   frontend redirects straight to the dashboard.

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '@forgetrack/auth';
import { requiredEnv } from '@forgetrack/api-client';
import PlanSelectionPage from '../pages/signup/PlanSelectionPage';
import { PLANS, type PlanId, type BillingCycle } from '../pages/signup/plans';
import type { SignupStatePayload } from './signup_.authenticate';

export const Route = createFileRoute('/signup')({
  component: SignupPage,
});

// ─── Types ──────────────────────────────────────────────────────────────────

type Phase =
  | 'plan-select'     // Unauthenticated: pick a plan (PlanSelectionPage)
  | 'bootstrapping'   // Post-auth: calling /signup/bootstrap
  | 'needs-workspace' // Post-auth: new user — show workspace name form
  | 'creating'        // Calling /signup/create-tenant (with plan from state)
  | 'redirecting'     // Leaving for dashboard or Stripe checkout
  | 'error';

interface BootstrapResponse {
  needsWorkspace: boolean;
  subdomain: string | null;
  tenantName: string | null;
  email: string;
  displayName: string;
}

interface CreateTenantRequest {
  workspaceName: string;
  planId: PlanId;
  billingCycle: BillingCycle;
}

interface CreateTenantResponse {
  tenantId: number;
  subdomain: string;
  tenantName: string;
  requiresCheckout: boolean;  // true for paid plans — frontend must redirect to Stripe
  checkoutUrl?: string;        // populated for paid plans
}

const API_URL = requiredEnv('VITE_API_URL', import.meta.env.VITE_API_URL);

// ─── Type guard for the recovered redirect state ───────────────────────────

function isSignupStatePayload(x: unknown): x is SignupStatePayload {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return o.v === 1 && typeof o.plan === 'string' && typeof o.cycle === 'string';
}

// ─── Component ──────────────────────────────────────────────────────────────

function SignupPage() {
  const {
    isInitialized,
    isAuthenticated,
    account,
    getAccessToken,
    redirectState,
    clearRedirectState,
  } = useAuth();

  const [phase, setPhase] = useState<Phase>('plan-select');
  const [workspaceName, setWorkspaceName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Plan selection — recovered from MSAL state via @forgetrack/auth.
  // The bridge page called login({ state: { v:1, plan, cycle, mode } }),
  // which MSAL round-tripped through Entra. The updated provider parses
  // it and exposes it here as a one-shot value.
  const planSelection = useMemo(() => {
    if (isSignupStatePayload(redirectState)) {
      return { planId: redirectState.plan, cycle: redirectState.cycle };
    }
    return null;
  }, [redirectState]);

  // ── Auth state → phase routing ─────────────────────────────────────────────
  useEffect(() => {
    if (!isInitialized) return;
    if (isAuthenticated && phase === 'plan-select') {
      setPhase('bootstrapping');
    }
  }, [isInitialized, isAuthenticated, phase]);

  // ── Post-auth: bootstrap tenant lookup ─────────────────────────────────────
  useEffect(() => {
    if (phase !== 'bootstrapping' || !account) return;

    (async () => {
      try {
        const accessToken = await getAccessToken();
        if (!accessToken) throw new Error('Unable to acquire access token');

        const res = await fetch(`${API_URL}/api/v1/signup/bootstrap`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) throw new Error(`Bootstrap failed (${res.status})`);
        const data: BootstrapResponse = await res.json();

        setDisplayName(data.displayName);
        setEmail(data.email);
        const firstName = data.displayName?.split(' ')[0] ?? '';
        setWorkspaceName(firstName ? `${firstName}'s Workspace` : '');

        if (!data.needsWorkspace && data.subdomain) {
          // Returning user with an existing workspace — go home.
          // Also clear any stale redirect state (e.g. they clicked a
          // signup link but already had an account).
          clearRedirectState();
          setPhase('redirecting');
          redirectToApp();
        } else {
          setPhase('needs-workspace');
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Bootstrap failed');
        setPhase('error');
      }
    })();
  }, [phase, account, getAccessToken, clearRedirectState]);

  // ── Create the workspace WITH the plan from state ──────────────────────────
  const handleCreateWorkspace = useCallback(async () => {
    const trimmed = workspaceName.trim();
    if (!trimmed) {
      setError('Give your workspace a name');
      return;
    }
    setPhase('creating');
    setError(null);
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) throw new Error('Unable to acquire access token');

      // Fall back to Free if somehow we got here without a plan selection
      // (shouldn't happen in the pre-auth flow, but defensive).
      const body: CreateTenantRequest = {
        workspaceName: trimmed,
        planId: planSelection?.planId ?? 'free',
        billingCycle: planSelection?.cycle ?? 'monthly',
      };

      const res = await fetch(`${API_URL}/api/v1/signup/create-tenant`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errBody = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(errBody.error ?? `Create failed (${res.status})`);
      }
      const tenant = (await res.json()) as CreateTenantResponse;

      // Consumed the redirect state — clear it so a page reload doesn't
      // replay the signup flow.
      clearRedirectState();

      setPhase('redirecting');

      // Paid plans: backend returned a Stripe checkout URL — redirect.
      if (tenant.requiresCheckout && tenant.checkoutUrl) {
        window.location.href = tenant.checkoutUrl;
        return;
      }

      // Free plans: straight to the dashboard on the new tenant subdomain.
      redirectToApp();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Workspace creation failed');
      setPhase('needs-workspace');
    }
  }, [workspaceName, getAccessToken, planSelection, clearRedirectState]);

  // ── Plan selection callback (pre-auth) ─────────────────────────────────────
  // PlanSelectionPage in pre-auth mode calls this when the user clicks
  // Continue. We navigate to the bridge page, which calls login() with
  // the plan encoded in MSAL state.
  const handlePlanSelectedPreAuth = useCallback(
    (planId: PlanId, cycle: BillingCycle) => {
      window.location.href = `/signup/authenticate?plan=${planId}&cycle=${cycle}&mode=signup`;
    },
    [],
  );

  // ── "I already have an account" — skip plan selection, go to bridge
  const handleSignInInstead = useCallback(() => {
    window.location.href = '/signup/authenticate?mode=signin';
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────

  // Unauthenticated: full-page plan selection.
  if (phase === 'plan-select') {
    return (
      <PlanSelectionPage
        mode="signup"
        onContinue={handlePlanSelectedPreAuth}
        onSignIn={handleSignInInstead}
      />
    );
  }

  // Post-auth: branded status / workspace form cards.
  return (
    <main className="ft-signup">
      <div className="ft-signup__bg" aria-hidden />
      <div className="ft-signup__grid" aria-hidden />
      <div className="ft-signup__glow" aria-hidden />

      <motion.div
        className="ft-signup__brand"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="ft-signup__wordmark">
          <svg viewBox="0 0 24 24" aria-hidden>
            <path d="M4 21 L12 3 L20 21 L16 21 L12 12 L8 21 Z" fill="currentColor" />
          </svg>
          <span>
            <span className="ft-signup__wordmark-forge">Forge</span>
            <span className="ft-signup__wordmark-track">Track</span>
          </span>
        </div>
      </motion.div>

      <AnimatePresence mode="wait">
        {phase === 'bootstrapping' && (
          <StatusCard
            key="boot"
            eyebrow="STEP 3 OF 3"
            title="Loading your workspace"
            subtitle="Checking for an existing tenant…"
          />
        )}
        {phase === 'creating' && (
          <StatusCard
            key="create"
            eyebrow="ALMOST THERE"
            title="Forging your workspace"
            subtitle="Provisioning tenant, user, and quota…"
          />
        )}
        {phase === 'redirecting' && (
          <StatusCard
            key="redir"
            eyebrow="READY"
            title="Taking you there"
            subtitle={
              planSelection && planSelection.planId !== 'free'
                ? 'Redirecting to secure checkout…'
                : 'Launching your workspace…'
            }
          />
        )}
        {phase === 'needs-workspace' && (
          <WorkspaceForm
            key="form"
            workspaceName={workspaceName}
            onChange={setWorkspaceName}
            onSubmit={handleCreateWorkspace}
            displayName={displayName}
            error={error}
            planSelection={planSelection}
          />
        )}
        {phase === 'error' && (
          <ErrorCard
            key="err"
            message={error ?? 'Something went wrong. Please try again.'}
          />
        )}
      </AnimatePresence>

      <style>{POSTAUTH_CSS}</style>
    </main>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function StatusCard({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow: string;
  title: string;
  subtitle: string;
}) {
  return (
    <motion.div
      className="ft-card"
      initial={{ opacity: 0, y: 16, filter: 'blur(8px)' }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      exit={{ opacity: 0, y: -16, filter: 'blur(8px)' }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="ft-card__eyebrow">{eyebrow}</div>
      <div className="ft-card__pulse" aria-hidden>
        <span />
        <span />
        <span />
      </div>
      <h1 className="ft-card__title">{title}</h1>
      <p className="ft-card__subtitle">{subtitle}</p>
    </motion.div>
  );
}

function WorkspaceForm({
  workspaceName,
  onChange,
  onSubmit,
  displayName,
  error,
  planSelection,
}: {
  workspaceName: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  displayName: string;
  error: string | null;
  planSelection: { planId: PlanId; cycle: BillingCycle } | null;
}) {
  const plan = useMemo(
    () => (planSelection ? PLANS.find((p) => p.id === planSelection.planId) : null),
    [planSelection],
  );

  return (
    <motion.div
      className="ft-card ft-card--form"
      initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      exit={{ opacity: 0, y: -16, filter: 'blur(8px)' }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      <motion.div
        className="ft-card__eyebrow"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15 }}
      >
        {displayName ? `WELCOME, ${displayName.split(' ')[0].toUpperCase()}` : 'WELCOME'}
      </motion.div>
      <h1 className="ft-card__title">Name your workspace</h1>
      <p className="ft-card__subtitle">
        This is your team's workspace name, shown across your dashboard and reports.
        You can rename it later in Settings.
      </p>

      {plan && (
        <div className="ft-plan-chip">
          <span className="ft-plan-chip__dot" />
          <span>
            {plan.name} · {planSelection?.cycle === 'annual' ? 'Annual' : 'Monthly'}
          </span>
        </div>
      )}

      <div className="ft-field">
        <label htmlFor="ws-name" className="ft-field__label">
          WORKSPACE NAME
        </label>
        <input
          id="ws-name"
          type="text"
          value={workspaceName}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
          maxLength={80}
          autoFocus
          className="ft-field__input"
          placeholder="Acme R&D"
        />
        {workspaceName && (
          <motion.div
            className="ft-field__hint"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            Your workspace:{' '}
            <span className="ft-field__hint-url">{workspaceName.trim()}</span>
          </motion.div>
        )}
      </div>

      {error && <div className="ft-error">{error}</div>}

      <button
        className="ft-btn ft-btn--primary"
        onClick={onSubmit}
        disabled={!workspaceName.trim()}
      >
        <span>Create workspace</span>
        <span className="ft-btn__arrow">→</span>
      </button>
    </motion.div>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <motion.div
      className="ft-card ft-card--error"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
    >
      <h1 className="ft-card__title">Something went wrong</h1>
      <p className="ft-card__subtitle">{message}</p>
      <button className="ft-btn ft-btn--primary" onClick={() => window.location.reload()}>
        <span>Try again</span>
        <span className="ft-btn__arrow">↻</span>
      </button>
    </motion.div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function redirectToApp() {
  const host = window.location.host;
  if (host.includes('localhost') || host.includes('127.0.0.1')) {
    window.location.href = '/dashboard';
    return;
  }
  const parts = host.split('.');
  const rootDomain = parts.length >= 2 ? parts.slice(-2).join('.') : host;
  window.location.href = `https://app.${rootDomain}/dashboard`;
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const POSTAUTH_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');

.ft-signup {
  --ft-ink:      #0A0E1A;
  --ft-ink-2:    #050910;
  --ft-copper:   #A0522D;
  --ft-copper-2: #B8623A;
  --ft-paper:    #F4F1EA;
  --ft-dim:      rgba(244, 241, 234, 0.58);
  --ft-faint:    rgba(244, 241, 234, 0.12);
  --ft-line:     rgba(244, 241, 234, 0.08);
  --ft-red:      #f87171;

  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 48px;
  padding: 24px;
  background: var(--ft-ink-2);
  color: var(--ft-paper);
  font-family: 'Space Grotesk', system-ui, sans-serif;
  overflow: hidden;
}

.ft-signup__bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #0A0E1A 0%, #050910 50%, #020408 100%);
  pointer-events: none;
}

.ft-signup__grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(to right, rgba(160, 82, 45, 0.04) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(160, 82, 45, 0.04) 1px, transparent 1px);
  background-size: 96px 96px;
  mask-image: radial-gradient(ellipse 80% 70% at 30% 45%, black, transparent 70%);
  -webkit-mask-image: radial-gradient(ellipse 80% 70% at 30% 45%, black, transparent 70%);
  pointer-events: none;
}

.ft-signup__glow {
  position: absolute;
  top: 30%;
  left: 18%;
  width: 900px;
  height: 900px;
  background: radial-gradient(
    circle,
    rgba(160, 82, 45, 0.22) 0%,
    rgba(160, 82, 45, 0.08) 30%,
    transparent 60%
  );
  filter: blur(60px);
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.ft-signup__brand { position: relative; z-index: 2; }
.ft-signup__wordmark {
  display: inline-flex;
  align-items: baseline;
  gap: 0.3rem;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 700;
  font-size: 1.6rem;
  letter-spacing: -0.035em;
  line-height: 1;
}
.ft-signup__wordmark svg {
  width: 0.9em;
  height: 0.9em;
  color: var(--ft-copper);
  transform: translateY(0.08em);
  filter: drop-shadow(0 0 12px rgba(160, 82, 45, 0.5));
}
.ft-signup__wordmark-forge { color: var(--ft-copper); }
.ft-signup__wordmark-track { color: var(--ft-paper); margin-left: -0.025em; }

.ft-card {
  position: relative;
  z-index: 2;
  width: min(480px, 92vw);
  padding: 40px 36px 36px;
  background: rgba(10, 14, 26, 0.72);
  border: 1px solid var(--ft-faint);
  border-radius: 18px;
  backdrop-filter: blur(24px) saturate(1.4);
  -webkit-backdrop-filter: blur(24px) saturate(1.4);
  box-shadow:
    0 0 0 1px rgba(160, 82, 45, 0.08),
    0 24px 80px -20px rgba(0, 0, 0, 0.8),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
}
.ft-card::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 18px;
  padding: 1px;
  background: linear-gradient(135deg, rgba(160, 82, 45, 0.4), transparent 40%, rgba(184, 98, 58, 0.25));
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
          mask-composite: exclude;
  pointer-events: none;
  opacity: 0.7;
}

.ft-card__eyebrow {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--ft-copper-2);
  margin-bottom: 16px;
  display: inline-flex;
  align-items: center;
}
.ft-card__eyebrow::before {
  content: '';
  display: inline-block;
  width: 1.4rem;
  height: 1px;
  background: currentColor;
  margin-right: 0.6rem;
  opacity: 0.6;
}

.ft-card__title {
  font-size: 28px;
  font-weight: 600;
  letter-spacing: -0.025em;
  line-height: 1.1;
  margin: 0 0 8px;
  color: var(--ft-paper);
}
.ft-card__subtitle {
  font-size: 14px;
  color: var(--ft-dim);
  margin: 0 0 24px;
  line-height: 1.55;
}

.ft-card__pulse {
  display: flex;
  gap: 6px;
  margin-bottom: 20px;
}
.ft-card__pulse span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--ft-copper);
  box-shadow: 0 0 8px rgba(160, 82, 45, 0.8);
  animation: ft-signup-pulse 1.2s ease-in-out infinite;
}
.ft-card__pulse span:nth-child(2) {
  animation-delay: 0.15s;
  background: var(--ft-copper-2);
}
.ft-card__pulse span:nth-child(3) {
  animation-delay: 0.3s;
  background: var(--ft-paper);
  box-shadow: 0 0 8px rgba(244, 241, 234, 0.5);
}
@keyframes ft-signup-pulse {
  0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
  40%           { opacity: 1;   transform: scale(1.1); }
}

.ft-plan-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  margin-bottom: 20px;
  background: rgba(160, 82, 45, 0.1);
  border: 1px solid rgba(160, 82, 45, 0.25);
  border-radius: 20px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.05em;
  color: var(--ft-paper);
}
.ft-plan-chip__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--ft-copper-2);
  box-shadow: 0 0 6px var(--ft-copper-2);
}

.ft-field { margin-bottom: 24px; }
.ft-field__label {
  display: block;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.22em;
  color: var(--ft-dim);
  margin-bottom: 8px;
}
.ft-field__input {
  width: 100%;
  padding: 14px 16px;
  background: rgba(5, 9, 16, 0.6);
  border: 1px solid var(--ft-faint);
  border-radius: 10px;
  color: var(--ft-paper);
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.ft-field__input:focus {
  border-color: var(--ft-copper-2);
  box-shadow: 0 0 0 3px rgba(160, 82, 45, 0.15);
}
.ft-field__input::placeholder { color: rgba(244, 241, 234, 0.3); }

.ft-field__hint {
  margin-top: 10px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: var(--ft-dim);
}
.ft-field__hint-url {
  color: var(--ft-copper-2);
  padding: 3px 8px;
  background: rgba(160, 82, 45, 0.1);
  border-radius: 4px;
}

.ft-error {
  padding: 10px 14px;
  margin-bottom: 16px;
  background: rgba(248, 113, 113, 0.08);
  border: 1px solid rgba(248, 113, 113, 0.25);
  border-radius: 8px;
  color: var(--ft-red);
  font-size: 13px;
  font-family: 'JetBrains Mono', monospace;
}

.ft-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 15px 20px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 15px;
  font-weight: 600;
  border-radius: 11px;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.25s, opacity 0.2s;
  border: none;
}
.ft-btn--primary {
  background: linear-gradient(135deg, var(--ft-copper), var(--ft-copper-2));
  color: var(--ft-paper);
  box-shadow:
    0 8px 24px -8px rgba(160, 82, 45, 0.55),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
}
.ft-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow:
    0 12px 32px -8px rgba(160, 82, 45, 0.7),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
}
.ft-btn--primary:active:not(:disabled) { transform: translateY(0); }
.ft-btn--primary:disabled { opacity: 0.4; cursor: not-allowed; box-shadow: none; }
.ft-btn__arrow { font-size: 18px; transition: transform 0.2s; }
.ft-btn--primary:hover:not(:disabled) .ft-btn__arrow { transform: translateX(3px); }
`;
